import React, { CSSProperties, useContext, useState } from 'react';
import { ThemeProvider } from 'emotion-theming';
import { FONT_SIZE, FONT_WEIGHT } from '@ordermentum/ui-static';
import { FormikErrors } from 'formik';
import S from '../../foundations/size';
import { FUTURO_TYPEFACES as F } from '../../foundations/font';
import { genThemedStyled, Theme, ThemeContext } from '../../utilities/theme';
import { getTheme } from './themes';

export type InputKeyboardEvent = React.KeyboardEvent<HTMLInputElement>;
export type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
export type InputFocusEvent = React.FocusEvent<HTMLInputElement>;

const ThemedStyled = genThemedStyled<ReturnType<typeof getTheme>>();

/**
 * Type used for formik setFieldValue function.
 *
 * https://formik.org/docs/api/formik#setfieldvalue-field-string-value-any-shouldvalidate-boolean--void
 */
export type OnChange = (tag: string, value: string) => void;
export type OnBlur = (tag: string, value: string) => void;
export type OnFocus = (tag: string, value: string) => void;
export type OnKeyDown = (e: InputKeyboardEvent) => void;

export type InputType =
  | 'text'
  | 'email'
  | 'number'
  | 'password'
  | 'search'
  | 'time';
export type InputSize = 'max' | 'large' | 'medium' | 'small' | 'xs' | 'auto';
export type InputHeight = 'large' | 'medium';
export type FontSize = 'large' | 'small' | 'xsmall';

export type InputProps = {
  // data
  hidden?: boolean;
  step?: any;
  isInactive?: boolean;
  max?: number;
  min?: number;
  maxLength?: number;
  readOnly?: boolean;
  required?: boolean;
  size?: InputSize;
  height?: InputHeight;
  tag: string;
  variant?: 'default' | 'code' | 'number';
  image?: string;
  theme?: Theme | 'lozenge-input';
  autoFocus?: boolean;
  innerRef?: React.Ref<HTMLInputElement>;
  placeholder?: string;
  type?: InputType;
  value: string | number;
  suffix?: string | React.ReactNode;
  // methods
  onBlur?: OnBlur;
  onChange?: OnChange;
  onFocus?: OnFocus;
  onKeyDown?: OnKeyDown;
  hasError?: boolean;
  errorMessage?: string;
  inline?: boolean;
  showTooltipOnFocus?: boolean;
  tooltip?: string;
  valueModifier?: (value: any) => any;
  // Some browsers try to create their own drop down for any field.
  // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
  // Set to "off" to disable this behaviour.
  // Set to "new-password" when the input is for creating a new password so the
  // browser or password manager won't try to insert an existing one.
  autoComplete?: 'off' | 'new-password';
  inputLabel?: string;
  hint?: string;
  isFuturo?: boolean;
  showBorder?: boolean;
  errorLabel?:
    | string
    | FormikErrors<any>
    | FormikErrors<any>[]
    | string[]
    | undefined;
  align?: CSSProperties['textAlign'];
  titleTooltip?: React.ReactNode;
};

const InputWrapper = ThemedStyled.div<{
  height?: string;
  size: string;
  inline?: boolean;
}>`
  align-items: center;
  display: flex;
  flex-direction: row;
  width: ${props => (props.size === 'max' ? '100%' : undefined)};
  position: relative;
`;

const Suffix = ThemedStyled.div<{
  inputTheme?: Theme | 'lozenge-input';
}>`
  font-size: ${FONT_SIZE.SMALL};
  font-weight: ${FONT_WEIGHT.MEDIUM};
  flex: ${props => (props.inputTheme ? 0 : '0 0 6.25rem')};
  margin-left: ${S.XXS};
`;

// @ts-expect-error props.theme.extras includes MozAppearance
export const InputElement = ThemedStyled.input<{
  inputSize?: InputSize;
  inputTheme?: Theme | 'lozenge-input';
  height?: InputHeight;
  readOnly: boolean;
  variant: 'default' | 'code' | 'number';
  image?: string;
  inputState: 'error' | 'inActive' | 'active';
  align: CSSProperties['textAlign'];
}>`
  height: ${props => props.theme.height[props.height || 'default']};
  border: ${props => props.theme.border[props.inputState]};
  box-shadow: ${props => props.theme.boxShadow[props.inputState]};
  padding: ${props => props.theme.padding};
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.color[props.inputState]};
  font-size: ${props => props.theme.fontSize};
  font-weight: ${props => props.theme.fontWeight};
  background: ${props => props.theme.background[props.inputState]};
   cursor: ${props =>
     props.theme.cursor[props.readOnly ? 'readOnly' : props.inputState]};
  width: ${props => props.theme.width[props.inputSize || 'default']};
  ${props => (props.readOnly ? props.theme.readOnly || {} : {})};
  ${props => props.theme.extras || {}};
  ${props => props.inputTheme === 'futuro' && F.LABEL}
  ${props => props.inputTheme === 'futuro-dark' && F.LABEL}
  ${props =>
    props.variant === 'code' &&
    'font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;'}

  ${props =>
    props.image
      ? `
    background-image: url("${props.image}");
    background-size: ${S.XL};
    background-repeat: no-repeat;
    background-position: 99% 50%;
    `
      : ''}
  ${props => {
    if (!props.align) {
      return null;
    }

    return {
      'text-align': props.align,
    };
  }}
`;

const TextWrapper = ThemedStyled.div({
  gap: S.XXXS,
  paddingBottom: S.XXXS,
  display: 'flex',
  flexDirection: 'column',
});

const LabelWrapper = ThemedStyled.label<{
  inputState: 'error' | 'inActive' | 'active';
}>`
  color: ${props => props.theme.labelColor[props.inputState]};
  ${F.LABEL_SMALL_BOLD};
`;

const HintWrapper = ThemedStyled.label<{
  readOnly: boolean;
  inputState: 'error' | 'inActive' | 'active';
}>`
  color: ${props => props.theme.hintColor[props.inputState]};
  ${F.LABEL_SMALL};
`;

const ErrorTextWrapper = ThemedStyled.div({
  paddingTop: S.XXXS,
  display: 'flex',
  flexDirection: 'column',
});

const ErrorLabelWrapper = ThemedStyled.label<{
  inputState: 'error' | 'inActive' | 'active';
}>`
  color: ${props => props.theme.errorLabelColor[props.inputState]};
  ${F.LABEL_SMALL_BOLD};
`;

const TooltipContainer = ThemedStyled.div`
    background-color: ${props => props.theme.tooltip.bg};
    border-radius: ${S.XXXS};
    position: absolute;
    top: -${S.XL};
    right: 0;
    padding: ${S.XXXS};
`;
const TooltipText = ThemedStyled.div`
    color: ${props => props.theme.tooltip.text};
    font-size: 0.875rem !important;
    ${F.LABEL}
`;

export const Input = (props: InputProps) => {
  const {
    // data
    type = 'text',
    tag,
    hidden = false,
    isInactive = false,
    step,
    size = 'medium',
    height,
    value,
    isFuturo = false,
    innerRef,
    maxLength,
    required = false,
    autoFocus = false,
    readOnly = false,
    suffix,
    // methods
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    hasError = false,
    placeholder,
    inline,
    valueModifier,
    theme,
    showTooltipOnFocus = false,
    tooltip,
    autoComplete,
    inputLabel,
    hint,
    errorLabel,
    variant = 'default',
    image,
    min,
    max,
    align,
    titleTooltip,
  } = props;

  const themeContext = useContext(ThemeContext);
  const [showTooltip, setShowTooltip] = useState(false);
  let currentTheme = theme ?? themeContext;
  // if the page is futuro, only limit the themes to futuro an futuro-dark
  if (isFuturo) {
    currentTheme = currentTheme === 'futuro-dark' ? 'futuro-dark' : 'futuro';
  }
  const getValue = (targetValue: string) =>
    valueModifier ? valueModifier(targetValue) : targetValue;

  const handleChange = (e: InputChangeEvent) => {
    if (onChange) {
      if (variant === 'number') {
        const regex = /^[0-9]*$/g;
        if (!regex.test(e.target.value)) return;
        // in case min prop is supplied return the toString value of it
        const quantity = e.target.value || '';

        if (min && Number(quantity) < min && e.target.value !== '') {
          onChange(tag, getValue(min.toString()));
          return;
        }

        if (max && Number(quantity) > max) {
          onChange(tag, getValue(max.toString()));
          return;
        }

        onChange(tag, getValue(quantity));
        return;
      }
      // Soft-numeric keystroke filter: when caller passes type="number",
      // block non-numeric input (digits + single decimal + optional minus).
      // Pairs with the GOV.UK type=text + inputmode=decimal pattern below.
      if (type === 'number' && !/^-?\d*\.?\d*$/.test(e.target.value)) return;
      onChange(tag, getValue(e.target.value));
    }
  };

  const handleBlur = (e: InputFocusEvent) => {
    if (onBlur) {
      if (variant === 'number' && min && e.target.value === '') {
        onBlur(tag, getValue(min.toString()));
        return;
      }
      onBlur(tag, getValue(e.target.value));
    }
    if (showTooltipOnFocus) setShowTooltip(false);
  };
  const handleFocus = (e: InputFocusEvent) => {
    if (onFocus) {
      onFocus(tag, getValue(e.target.value));
    }
    if (showTooltipOnFocus) setShowTooltip(true);
  };

  const inputState = hasError ? 'error' : isInactive ? 'inActive' : 'active'; // eslint-disable-line no-nested-ternary

  return (
    <ThemeProvider theme={getTheme(currentTheme)}>
      {inputLabel && (
        <TextWrapper>
          <div className="flex gap-xxs">
            <LabelWrapper htmlFor={tag} {...{ inputState }}>
              {inputLabel}
            </LabelWrapper>
            {titleTooltip}
          </div>
          {hint && (
            <HintWrapper {...{ inputState, readOnly }}>{hint}</HintWrapper>
          )}
        </TextWrapper>
      )}
      <InputWrapper inline={inline} size={size}>
        {showTooltip && (
          <TooltipContainer>
            <TooltipText>{tooltip}</TooltipText>
          </TooltipContainer>
        )}
        <InputElement
          {...{
            inputSize: size,
            height,
            inputState,
            readOnly,
            inputTheme: currentTheme,
            image,
            variant,
            // type="number" → soft numeric (text + decimal inputmode).
            // variant="number" → strict integer (text + numeric inputmode + pattern).
            // Per GOV.UK: native type="number" is hostile to NVDA, Dragon, and
            // can silently corrupt long numeric strings (e.g. credit cards).
            // Use camelCase inputMode so Emotion's is-prop-valid forwards it.
            ...(variant === 'number' && {
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }),
            ...(type === 'number' &&
              variant !== 'number' && {
                inputMode: 'decimal',
              }),
          }}
          key={tag}
          id={tag}
          aria-label={tag}
          maxLength={maxLength}
          // hide the number input arrows on readonly
          max={props.max}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          min={props.min}
          type={readOnly || type === 'number' ? 'text' : type}
          name={tag}
          step={step}
          ref={innerRef}
          value={value}
          hidden={hidden}
          disabled={isInactive}
          onChange={handleChange}
          variant={variant}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          autoComplete={autoComplete}
          align={align}
        />
        {suffix ? (
          <Suffix
            {...{
              inputTheme: currentTheme,
            }}
          >
            {suffix}
          </Suffix>
        ) : null}
      </InputWrapper>
      {hasError && (
        <ErrorTextWrapper>
          <ErrorLabelWrapper {...{ inputState }}>
            {errorLabel}
          </ErrorLabelWrapper>
        </ErrorTextWrapper>
      )}
    </ThemeProvider>
  );
};

export { TinyInput } from './TinyInput';
